import React from 'react';
import { Trophy, Flag, Timer, Target, Zap, Quote, ArrowRight, PlayCircle } from 'lucide-react';
import { openSportsFamilyEnquiry } from './TravelTutorSection';

/**
 * "Trusted by Elite Sporting Families" — discretionary positioning.
 * No names, but explicit categories of elite young athletes previously tutored.
 * Emphasises Dr. Michael's athlete-tutor USP.
 */

const TEAM_GB_VIDEO_URL = 'https://customer-assets-m6fa6gv7.emergentagent.net/job_30b73cfb-2a7e-4a88-a15f-a7e3cd8fbd24/artifacts/zto02mtx_Screen_Recording_20260926_120028_Instagram.mp4';

const cohorts = [
  {
    icon: Flag,
    tag: 'Football',
    title: 'Premier League Academy Children',
    body: 'Tutored children of Premier League footballers within elite youth academy pathways. Understanding the demands of academy training, camps, and international squad call-ups.',
    image: 'https://images.unsplash.com/photo-1622659097509-4d56de14539e?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80'
  },
  {
    icon: Zap,
    tag: 'Motorsport',
    title: 'Formula 1 Junior Programmes',
    body: 'Supported young drivers on the Formula 1 pathway — karting through junior single-seaters. Structuring academic work around European race calendars and testing days.',
    image: 'https://images.unsplash.com/photo-1591445522523-beab22a24d9f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80'
  },
  {
    icon: Target,
    tag: 'Tennis',
    title: 'Junior International Players',
    body: 'Worked with tennis players on the ITF junior circuit. Delivering focused academic sessions between training blocks, tournaments, and long-haul travel.',
    image: 'https://images.unsplash.com/photo-1714840961998-8d6c02ace00b?crop=entropy&cs=srgb&fm=jpg&w=1200&q=80'
  }
];

const EliteAthletesSection = () => {
  return (
    <section
      data-testid="elite-athletes-section"
      style={{
        background: '#FFFCF5',
        padding: 'clamp(4rem, 8vw, 7rem) 2rem',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Subtle noise texture */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)'/%3E%3C/svg%3E")`,
        opacity: 0.03, pointerEvents: 'none'
      }} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', position: 'relative' }}>
        {/* Editorial header */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '3rem',
          marginBottom: '4.5rem',
          alignItems: 'end'
        }}>
          <div>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em',
              textTransform: 'uppercase', color: '#C5A059',
              marginBottom: '1.25rem', fontFamily: "'Inter', sans-serif"
            }}>
              <span style={{ width: '32px', height: '1px', background: '#C5A059' }} />
              The Athlete-Educator
            </div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(2rem, 4.5vw, 3.4rem)',
              fontWeight: 700, lineHeight: 1.1,
              margin: 0, color: '#0F172A',
              letterSpacing: '-0.02em'
            }}>
              Trusted by elite<br />
              <span style={{ color: '#C5A059', fontStyle: 'italic' }}>sporting families.</span>
            </h2>
          </div>

          <div>
            <p style={{
              fontSize: '1.05rem', lineHeight: 1.8,
              color: '#475569', margin: 0,
              fontFamily: "'Inter', sans-serif"
            }}>
              As a Team GB Masters sprinter myself, I understand — from the inside — what it takes for a
              young person to balance elite training with the unforgiving demands of British education.
              Early alarms, missed classes, competition travel, injury weeks, and the quiet pressure to
              still deliver academically. This is where I do my best work.
            </p>
          </div>
        </div>

        {/* Pull-quote — the personal angle, with Team GB video proof */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '0',
          marginBottom: '4rem',
          background: '#0F172A',
          borderLeft: '4px solid #C5A059',
          overflow: 'hidden'
        }}>
          {/* Left: quote */}
          <div style={{ padding: 'clamp(2rem, 4vw, 3rem)', color: '#FFFCF5' }}>
            <div style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#C5A059', color: '#0F172A',
              padding: '0.35rem 0.75rem',
              fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              fontFamily: "'Inter', sans-serif",
              marginBottom: '1.25rem'
            }}>
              <Trophy size={12} />
              400m Gold · European Masters League · Albania
            </div>
            <Quote size={32} color="#C5A059" style={{ opacity: 0.5, marginBottom: '1rem' }} />
            <p style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(1.15rem, 2vw, 1.55rem)',
              lineHeight: 1.45, fontStyle: 'italic',
              margin: '0 0 1.25rem 0', fontWeight: 400, letterSpacing: '-0.01em'
            }}>
              "The best tutor for an elite young athlete is one who's lived the diary.
              I race for Team GB. I know what a competition week does to a study plan —
              and how to build one that survives it."
            </p>
            <div style={{
              fontSize: '0.8rem', fontWeight: 600, letterSpacing: '0.15em',
              textTransform: 'uppercase', color: '#C5A059',
              fontFamily: "'Inter', sans-serif"
            }}>
              — Dr. Michael Omakobia
            </div>
          </div>

          {/* Right: video proof */}
          <div
            data-testid="team-gb-video-wrap"
            style={{
              position: 'relative',
              background: '#0F172A',
              minHeight: '320px',
              display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <video
              data-testid="team-gb-video"
              src={TEAM_GB_VIDEO_URL}
              controls
              playsInline
              preload="metadata"
              style={{
                width: '100%', height: '100%',
                objectFit: 'cover',
                display: 'block'
              }}
            >
              Your browser does not support HTML5 video.
            </video>
            <div style={{
              position: 'absolute', top: '1rem', left: '1rem',
              display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
              background: 'rgba(15, 23, 42, 0.85)', color: '#C5A059',
              padding: '0.4rem 0.85rem',
              fontSize: '0.65rem', fontWeight: 700,
              letterSpacing: '0.15em', textTransform: 'uppercase',
              fontFamily: "'Inter', sans-serif",
              backdropFilter: 'blur(6px)',
              pointerEvents: 'none'
            }}>
              <PlayCircle size={12} />
              Team GB · Live footage
            </div>
          </div>
        </div>

        {/* Cohort cards — editorial 3-column */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: '2rem',
          marginBottom: '4rem'
        }}>
          {cohorts.map((c, i) => (
            <div
              key={i}
              data-testid={`elite-cohort-${i}`}
              style={{
                background: '#FFFCF5',
                border: '1px solid rgba(15, 23, 42, 0.08)',
                overflow: 'hidden',
                display: 'flex', flexDirection: 'column',
                transition: 'transform 0.4s ease, box-shadow 0.4s ease'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-6px)';
                e.currentTarget.style.boxShadow = '0 20px 48px rgba(15, 23, 42, 0.15)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {/* Image with duotone overlay */}
              <div style={{
                position: 'relative', height: '220px', overflow: 'hidden',
                background: '#0F172A'
              }}>
                <img
                  src={c.image} alt={c.tag}
                  loading="lazy"
                  style={{
                    width: '100%', height: '100%',
                    objectFit: 'cover',
                    filter: 'grayscale(35%) contrast(1.05) brightness(0.95)',
                    transition: 'transform 0.6s ease'
                  }}
                />
                {/* Gold gradient wash for palette cohesion */}
                <div style={{
                  position: 'absolute', inset: 0,
                  background: 'linear-gradient(180deg, rgba(15,23,42,0.15) 0%, rgba(15,23,42,0.55) 100%)',
                  pointerEvents: 'none'
                }} />
                <div style={{
                  position: 'absolute', top: '1rem', left: '1rem',
                  display: 'inline-flex', alignItems: 'center', gap: '0.4rem',
                  background: '#C5A059', color: '#0F172A',
                  padding: '0.35rem 0.75rem',
                  fontSize: '0.65rem', fontWeight: 700,
                  letterSpacing: '0.15em', textTransform: 'uppercase',
                  fontFamily: "'Inter', sans-serif"
                }}>
                  <c.icon size={11} />
                  {c.tag}
                </div>
              </div>

              {/* Body */}
              <div style={{ padding: '1.75rem', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <h3 style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: '1.35rem', fontWeight: 700,
                  color: '#0F172A', margin: '0 0 0.85rem 0',
                  lineHeight: 1.2, letterSpacing: '-0.01em'
                }}>
                  {c.title}
                </h3>
                <p style={{
                  color: '#475569', fontSize: '0.95rem',
                  lineHeight: 1.7, margin: 0,
                  fontFamily: "'Inter', sans-serif",
                  flex: 1
                }}>
                  {c.body}
                </p>
              </div>
            </div>
          ))}
        </div>

        {/* Sports-Family CTA */}
        <div
          data-testid="sports-family-cta"
          style={{
            marginTop: '3rem',
            marginBottom: '2rem',
            padding: 'clamp(2rem, 4vw, 3rem)',
            background: 'rgba(197, 160, 89, 0.06)',
            border: '1px solid rgba(197, 160, 89, 0.35)',
            textAlign: 'center'
          }}
        >
          <div style={{
            fontSize: '0.7rem', fontWeight: 600, letterSpacing: '0.2em',
            textTransform: 'uppercase', color: '#C5A059',
            marginBottom: '0.75rem', fontFamily: "'Inter', sans-serif"
          }}>
            Placements for Sporting Families
          </div>
          <h3 style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 'clamp(1.4rem, 3vw, 2rem)', fontWeight: 700,
            color: '#0F172A', margin: '0 0 0.75rem 0',
            letterSpacing: '-0.02em', lineHeight: 1.2
          }}>
            Have a young athlete who needs to <span style={{ color: '#C5A059', fontStyle: 'italic' }}>balance both worlds?</span>
          </h3>
          <p style={{
            color: '#475569', margin: '0 auto 1.75rem', maxWidth: '540px',
            fontSize: '1rem', lineHeight: 1.7, fontFamily: "'Inter', sans-serif"
          }}>
            Whether the pathway is football academy, Formula 1 junior, tennis circuit, or elsewhere —
            I'll travel with your family and hold the academic side steady while your child chases the dream.
          </p>
          <button
            onClick={openSportsFamilyEnquiry}
            data-testid="sports-family-enquiry-btn"
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.5rem',
              background: '#0F172A', color: '#FFFCF5',
              padding: '1rem 2rem', border: '1px solid #0F172A',
              cursor: 'pointer', fontWeight: 700,
              fontFamily: "'Inter', sans-serif",
              letterSpacing: '0.05em', textTransform: 'uppercase',
              fontSize: '0.85rem', borderRadius: '3px',
              transition: 'all 0.3s ease'
            }}
            onMouseEnter={(e) => { e.currentTarget.style.background = '#C5A059'; e.currentTarget.style.color = '#0F172A'; e.currentTarget.style.borderColor = '#C5A059'; }}
            onMouseLeave={(e) => { e.currentTarget.style.background = '#0F172A'; e.currentTarget.style.color = '#FFFCF5'; e.currentTarget.style.borderColor = '#0F172A'; }}
          >
            Enquire About Sports-Family Placement
            <ArrowRight size={18} />
          </button>
        </div>

        {/* Personal cred strip */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '1.5rem',
          padding: '2rem',
          background: 'rgba(197, 160, 89, 0.06)',
          borderTop: '1px solid rgba(197, 160, 89, 0.25)',
          borderBottom: '1px solid rgba(197, 160, 89, 0.25)'
        }}>
          {[
            { icon: Trophy, stat: 'Team GB', label: 'International sprinter' },
            { icon: Timer, stat: '100m & 200m', label: 'Sprint specialist' },
            { icon: Flag, stat: '10+ nations', label: 'Competed internationally' },
            { icon: Target, stat: 'Masters Level', label: 'Active competitor' }
          ].map((s, i) => (
            <div
              key={i}
              data-testid={`athlete-stat-${i}`}
              style={{ textAlign: 'center' }}
            >
              <s.icon size={22} color="#C5A059" style={{ marginBottom: '0.5rem' }} />
              <div style={{
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: '1.25rem', fontWeight: 700,
                color: '#0F172A', letterSpacing: '-0.01em', marginBottom: '0.2rem'
              }}>
                {s.stat}
              </div>
              <div style={{
                color: '#64748B', fontSize: '0.72rem',
                letterSpacing: '0.15em', textTransform: 'uppercase',
                fontFamily: "'Inter', sans-serif", fontWeight: 500
              }}>
                {s.label}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default EliteAthletesSection;
